import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import type { DigestVersion } from "../lib/api";

interface VersionSelectorProps {
  videoId: string;
  versions: DigestVersion[];
  /** Current selection: a specific version string, or undefined for "latest". */
  current: string | undefined;
}

/**
 * Native <select> wrapped for brand styling. "Latest" pins to whatever the
 * newest version is and routes to /videos/{id} (no version segment); a
 * specific pick routes to /videos/{id}/digests/{version} so the URL is
 * shareable. Latest is the default when no version is in the URL.
 */
export function VersionSelector({
  videoId,
  versions,
  current,
}: VersionSelectorProps) {
  const navigate = useNavigate();

  if (versions.length === 0) return null;

  const latestVersion = versions[0].version;
  const effectiveValue = current ?? "latest";
  const showingVersion = current ?? latestVersion;
  const matched = versions.find((v) => v.version === showingVersion);

  const onChange = (value: string) => {
    if (value === "latest") {
      navigate(`/youtube/videos/${videoId}`);
    } else {
      navigate(`/youtube/videos/${videoId}/digests/${value}`);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 text-sm text-ink-muted">
      <label className="flex items-center gap-2">
        <span>Reading</span>
        <span className="relative">
          <select
            value={effectiveValue}
            onChange={(e) => onChange(e.target.value)}
            className="appearance-none rounded border border-rule bg-surface py-1 pl-2.5 pr-7 text-sm font-medium text-ink focus:border-brand-indigo focus:outline-none"
          >
            <option value="latest">
              Latest ({formatVersionLabel(latestVersion)})
            </option>
            {versions.map((v) => (
              <option key={v.version} value={v.version}>
                {formatVersionLabel(v.version)}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted"
          />
        </span>
      </label>
      <span className="text-ink-muted/70">·</span>
      <span>
        {versions.length} version{versions.length === 1 ? "" : "s"}
      </span>
      {matched && (
        <>
          <span className="text-ink-muted/70">·</span>
          <span>generated {formatRelative(matched.generatedAt)}</span>
        </>
      )}
    </div>
  );
}

function formatVersionLabel(version: string): string {
  // Versions are filename stems like "2026-05-14T070042Z" — show as date + time
  const m = version.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
  );
  if (!m) return version;
  const [, y, mo, d, h, mi] = m;
  const date = new Date(
    Date.UTC(+y, +mo - 1, +d, +h, +mi),
  );
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelative(iso: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24));
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}
